import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call } from "../api/client";
import { keys, useSystems, useUsers } from "../api/queries";
import type { CharacterView } from "../api/types";
import { text } from "../text";
import { useCurrentUser } from "./AuthPages";

export function NewCharacterPage() {
  const systems = useSystems();
  const user = useCurrentUser();
  const users = useUsers(user.role === "admin");
  const navigate = useNavigate();
  const client = useQueryClient();
  const [systemId, setSystemId] = useState("");
  const [name, setName] = useState("");
  const [owner, setOwner] = useState(user.id);
  const [packages, setPackages] = useState<string[]>([]);
  const [books, setBooks] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const system = systems.data?.find((s) => s.id === systemId) ?? systems.data?.[0];
  useEffect(() => {
    if (!system) return;
    setSystemId(system.id);
    setPackages(system.packages.filter((p) => p.default).map((p) => p.id));
    setBooks(null);
  }, [system?.id]);

  const allBooks = system?.packages.filter((p) => packages.includes(p.id)).flatMap((p) => p.books) ?? [];
  const activeBooks = books ?? allBooks.filter((b) => b.required || b.default).map((b) => b.code);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!system) return;
    setError(null);
    try {
      const profile = system.profiles[0];
      const character = await call<CharacterView>(
        api.POST("/api/characters", {
          body: {
            system: system.id,
            name,
            ownerId: owner,
            parameters: {},
            settings: {
              packages: packages.map((id) => ({ id })),
              excludedPackages: system.packages.filter((p) => p.default && !packages.includes(p.id)).map((p) => p.id),
              books: books,
              options: profile ? { profile: profile.id, profileVersion: profile.version, values: profile.values } : { values: {} },
            },
          },
        }),
      );
      await client.invalidateQueries({ queryKey: keys.characters });
      navigate(system.wizardSteps.length > 0 ? `/c/${character.id}/assistent` : `/c/${character.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  if (systems.isLoading) return <p className="muted">Lade …</p>;
  return (
    <form className="stack" onSubmit={submit}>
      <h1>Neuer Charakter</h1>
      <label className="field">
        <span>Regelsystem</span>
        <select value={systemId} onChange={(e) => setSystemId(e.target.value)}>
          {systems.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {text(s.name)}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
      </label>
      {user.role === "admin" && users.data && (
        <label className="field">
          <span>Spieler</span>
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            {users.data.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>
      )}
      {system && system.packages.length > 1 && (
        <fieldset className="card">
          <legend>Datenpakete</legend>
          {system.packages.map((p) => (
            <label key={p.id} className="option">
              <input
                type="checkbox"
                checked={packages.includes(p.id)}
                onChange={(e) => setPackages(e.target.checked ? [...packages, p.id] : packages.filter((x) => x !== p.id))}
              />
              <span>
                {text(p.title)} <span className="muted">{p.version}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {allBooks.length > 1 && (
        <fieldset className="card">
          <legend>Quellen</legend>
          {allBooks.map((b) => (
            <label key={b.code} className="option">
              <input
                type="checkbox"
                disabled={b.required}
                checked={activeBooks.includes(b.code)}
                onChange={(e) => setBooks(e.target.checked ? [...activeBooks, b.code] : activeBooks.filter((x) => x !== b.code))}
              />
              <span>
                {text(b.title)} <span className="muted">{b.code}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {error && <p className="error">{error}</p>}
      <button type="submit" className="button primary">
        Anlegen
      </button>
    </form>
  );
}
